"use client";

import { motion } from "framer-motion";

import { cn } from "@/lib/utils/utils";

interface MaskedLinesProps {
  /** One entry per rendered line. Line breaks are a design decision, not a
   *  reflow accident, so they are authored rather than measured. */
  lines: string[];
  className?: string;
  as?: "h1" | "h2";
  /**
   * Play on mount instead of on entry. The page title is already on screen
   * when the route renders, and a viewport-triggered reveal there is a race:
   * if the observer does not fire, the heading stays behind its own mask and
   * the page opens with a hole where the title should be.
   */
  immediate?: boolean;
}

/**
 * A headline whose lines rise out from behind their own clip.
 *
 * Used once per page, on the title. The mask is the `overflow-hidden` span; the
 * inner span is the only thing that moves, which keeps the whole effect on the
 * compositor.
 */
const MaskedLines = ({
  lines,
  className,
  as: Tag = "h2",
  immediate,
}: MaskedLinesProps) => (
  <Tag className={cn(className)}>
    {lines.map((line, i) => (
      <span key={line} className="block overflow-hidden">
        <motion.span
          className="block"
          initial={{ y: "105%" }}
          {...(immediate
            ? { animate: { y: 0 } }
            : { whileInView: { y: 0 }, viewport: { once: true } })}
          transition={{
            duration: 0.9,
            ease: [0.16, 1, 0.3, 1],
            delay: i * 0.08,
          }}
        >
          {line}
        </motion.span>
      </span>
    ))}
  </Tag>
);

export default MaskedLines;
